import styled, { css } from "styled-components";
import { Link } from "react-router-dom";

import Button from "../../components/Buttons/Button";
import { Input as SharedInput } from "../../components/IOSCompatibleInput/IOSCompatibleInput.style";
import { ModalOverlayBase, ModalSurfaceBase } from "../../components/Modals/ModalPrimitives";
import { pxToRem, withAlpha } from "../../styles/utils";

export const CoursesPageShell = styled.div`
  width: 100%;
  max-width: ${pxToRem(900)};
`;

export const CoursesPageTitle = styled.h1`
  margin: 0;
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.xl)};
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: -0.02em;
`;

export const CoursesPageLead = styled.p`
  max-width: ${pxToRem(620)};
  margin: ${({ theme }) => pxToRem(theme.spacing.xxs)} 0 ${({ theme }) => pxToRem(theme.spacing.xl)};
  color: ${({ theme }) => withAlpha(theme.colors.ink, 0.64)};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  line-height: 1.45;
`;

export const CourseList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => pxToRem(theme.spacing.xs)};
`;

const courseSurface = css`
  display: block;
  width: 100%;
  padding: ${({ theme }) => pxToRem(theme.spacing.md)} ${({ theme }) => pxToRem(theme.spacing.lg)};
  border: ${pxToRem(1)} solid ${({ theme }) => theme.borders.outline};
  border-radius: ${({ theme }) => pxToRem(theme.radii.lg)};
  background: ${({ theme }) => theme.colors.white};
  color: inherit;
`;

export const MyCourseCard = styled(Link)`
  ${courseSurface}
  text-decoration: none;
  transition: background-color 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.brandSubtle};
  }

  &:focus-visible {
    outline: ${pxToRem(2)} solid ${({ theme }) => theme.colors.brand};
    outline-offset: ${pxToRem(2)};
  }
`;

export const CatalogCourseCard = styled.article`
  ${courseSurface}
`;

export const CourseTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => pxToRem(theme.spacing.md)};

  @media (max-width: 720px) {
    flex-direction: column;
  }
`;

export const CourseMain = styled.div`
  min-width: 0;
`;

export const CourseMeta = styled.div`
  color: ${({ theme }) => withAlpha(theme.colors.ink, 0.42)};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.xs)};
  line-height: 1.45;
`;

export const CourseTitle = styled.h2`
  margin: ${({ theme }) => pxToRem(theme.spacing.xxs)} 0 0;
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.md)};
  font-weight: 600;
  line-height: 1.35;
  letter-spacing: -0.01em;
`;

export const CourseDescription = styled.p`
  display: -webkit-box;
  overflow: hidden;
  margin: ${({ theme }) => pxToRem(theme.spacing.xs)} 0 0;
  color: ${({ theme }) => withAlpha(theme.colors.ink, 0.64)};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  line-height: 1.45;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
`;

export const CourseSide = styled.div`
  flex: 0 0 auto;
  text-align: right;

  @media (max-width: 720px) {
    text-align: left;
  }
`;

export const CoursePrice = styled.div`
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.lg)};
  font-weight: 600;
  line-height: 1.3;
  letter-spacing: -0.01em;
  white-space: nowrap;
`;

export const CourseFoot = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => pxToRem(theme.spacing.sm)};
  flex-wrap: wrap;
  margin-top: ${({ theme }) => pxToRem(theme.spacing.sm)};
  padding-top: ${({ theme }) => pxToRem(theme.spacing.sm)};
  border-top: ${pxToRem(1)} solid ${({ theme }) => theme.borders.divider};
`;

export const HomeworkTrack = styled.span`
  flex: 1 1 ${pxToRem(180)};
  height: ${pxToRem(6)};
  overflow: hidden;
  border-radius: ${({ theme }) => pxToRem(theme.radii.pill)};
  background: ${({ theme }) => withAlpha(theme.colors.ink, 0.07)};
`;

export const HomeworkProgress = styled.span<{ $percent: number }>`
  display: block;
  width: ${({ $percent }) => `${Math.max(0, Math.min(100, $percent))}%`};
  height: 100%;
  border-radius: inherit;
  background: ${({ theme }) => theme.colors.brand};
`;

export const CourseFootText = styled.span`
  color: ${({ theme }) => withAlpha(theme.colors.ink, 0.64)};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  line-height: 1.45;
`;

export const CourseFootStrong = styled.strong`
  color: ${({ theme }) => theme.colors.ink};
  font-weight: 500;
`;

export const CourseChevron = styled.span`
  width: ${pxToRem(10)};
  height: ${pxToRem(10)};
  margin-left: auto;
  flex: 0 0 auto;
  border-top: ${pxToRem(2)} solid ${({ theme }) => withAlpha(theme.colors.ink, 0.42)};
  border-right: ${pxToRem(2)} solid ${({ theme }) => withAlpha(theme.colors.ink, 0.42)};
  transform: rotate(45deg);
`;

export const CourseChip = styled.span<{ $done?: boolean }>`
  display: inline-flex;
  align-items: center;
  min-height: ${pxToRem(26)};
  padding: 0 ${({ theme }) => pxToRem(theme.spacing.xs)};
  border-radius: ${({ theme }) => pxToRem(theme.radii.sm)};
  background: ${({ $done, theme }) =>
    $done ? withAlpha(theme.colors.success, 0.14) : withAlpha(theme.colors.ink, 0.06)};
  color: ${({ $done, theme }) =>
    $done ? theme.colors.successText : withAlpha(theme.colors.ink, 0.64)};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.xs)};
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
`;

export const CatalogGroup = styled.section`
  & + & {
    margin-top: ${({ theme }) => pxToRem(theme.spacing.xl)};
  }
`;

export const CatalogGroupTitle = styled.h2`
  display: flex;
  align-items: baseline;
  gap: ${({ theme }) => pxToRem(theme.spacing.xs)};
  margin: 0 0 ${({ theme }) => pxToRem(theme.spacing.xs)};
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  font-weight: 600;
  line-height: 1.45;
`;

export const CatalogGroupCount = styled.span`
  color: ${({ theme }) => withAlpha(theme.colors.ink, 0.42)};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  font-weight: 400;
`;

export const CourseActionSlot = styled.span`
  display: flex;
  align-items: center;
  margin-left: auto;
`;

export const CourseActionButton = styled(Button).attrs({ size: "sm" })`
  width: auto;
  min-width: 0;
  max-width: none;
  font-weight: 500;
`;

export const QuietCourseLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: ${({ theme }) => pxToRem(theme.controlHeight.sm)};
  padding: 0 ${({ theme }) => pxToRem(theme.spacing.sm)};
  border: ${pxToRem(1)} solid ${({ theme }) => theme.borders.outline};
  border-radius: ${({ theme }) => pxToRem(theme.radii.md)};
  background: transparent;
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  font-weight: 500;
  line-height: 1;
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    background: ${({ theme }) => theme.colors.inkSurface};
  }

  &:focus-visible {
    outline: ${pxToRem(2)} solid ${({ theme }) => theme.colors.brand};
    outline-offset: ${pxToRem(2)};
  }
`;

export const EmptyCourses = styled.div`
  padding: ${({ theme }) => pxToRem(theme.spacing.xxl)};
  border: ${pxToRem(1)} dashed ${({ theme }) => theme.borders.outline};
  border-radius: ${({ theme }) => pxToRem(theme.radii.lg)};
  text-align: center;
`;

export const EmptyCoursesTitle = styled.strong`
  display: block;
  margin-bottom: ${({ theme }) => pxToRem(theme.spacing.xxs)};
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.md)};
  font-weight: 500;
`;

export const EmptyCoursesText = styled.span`
  color: ${({ theme }) => withAlpha(theme.colors.ink, 0.42)};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  line-height: 1.45;
`;

export const EmptyCoursesAction = styled.div`
  margin-top: ${({ theme }) => pxToRem(theme.spacing.sm)};
`;

export const PrimaryCourseLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: ${({ theme }) => pxToRem(theme.controlHeight.sm)};
  padding: 0 ${({ theme }) => pxToRem(theme.spacing.sm)};
  border-radius: ${({ theme }) => pxToRem(theme.radii.md)};
  background: ${({ theme }) => theme.colors.brand};
  color: ${({ theme }) => theme.colors.white};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  font-weight: 500;
  line-height: 1;
  text-decoration: none;

  &:hover {
    background: ${({ theme }) => theme.colors.brandDark};
  }

  &:focus-visible {
    outline: ${pxToRem(2)} solid ${({ theme }) => theme.colors.brand};
    outline-offset: ${pxToRem(2)};
  }
`;

export const DemoLoadingState = styled.div`
  padding: ${({ theme }) => pxToRem(theme.spacing.xl)} 0;
  color: ${({ theme }) => withAlpha(theme.colors.ink, 0.42)};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
`;

export const ApplicationOverlay = styled(ModalOverlayBase)``;

export const ApplicationModal = styled(ModalSurfaceBase)`
  width: min(100%, ${pxToRem(480)});
  padding: ${({ theme }) => pxToRem(theme.spacing.xl)};
`;

export const ApplicationTitle = styled.h3`
  margin: 0 0 ${({ theme }) => pxToRem(theme.spacing.xxs)};
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.lg)};
  font-weight: 600;
  line-height: 1.35;
`;

export const ApplicationSub = styled.p`
  margin: 0 0 ${({ theme }) => pxToRem(theme.spacing.lg)};
  color: ${({ theme }) => withAlpha(theme.colors.ink, 0.64)};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  line-height: 1.45;
`;

export const ApplicationField = styled.div`
  margin-bottom: ${({ theme }) => pxToRem(theme.spacing.sm)};
`;

export const ApplicationFieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => pxToRem(theme.spacing.sm)};
  margin-bottom: ${({ theme }) => pxToRem(theme.spacing.sm)};

  ${ApplicationField} {
    margin-bottom: 0;
  }

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

export const ApplicationLabel = styled.label`
  display: block;
  margin-bottom: ${({ theme }) => pxToRem(theme.spacing.xxs)};
  color: ${({ theme }) => withAlpha(theme.colors.ink, 0.64)};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
`;

export const ApplicationInput = styled(SharedInput)``;

export const ApplicationError = styled.div`
  margin-top: ${({ theme }) => pxToRem(theme.spacing.xxs)};
  color: ${({ theme }) => theme.colors.danger};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.xs)};
  line-height: 1.35;
`;

export const ApplicationActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => pxToRem(theme.spacing.xs)};
  margin-top: ${({ theme }) => pxToRem(theme.spacing.lg)};
`;

export const ApplicationCancelButton = styled(Button).attrs({
  variant: "secondary",
  size: "sm",
})`
  width: auto;
  min-width: 0;
  max-width: none;
  font-weight: 500;
`;
